import { FileQuestion, Search, Frown, type LucideIcon } from "lucide-react";
import { useTranslation } from "@/lib/i18n";

function ProblemCard({
  icon: Icon,
  number,
  title,
  description,
  staggerClass,
}: {
  icon: LucideIcon;
  number: string;
  title: string;
  description: string;
  staggerClass: string;
}) {
  return (
    <div
      className={`animate-fade-up ${staggerClass} relative glass-card hover-lift rounded-2xl border-l-2 border-destructive/30 hover:border-destructive p-6 sm:p-8 overflow-hidden transition-colors`}
    >
      {/* Decorative large number */}
      <span
        className="absolute -top-2 -right-2 text-[6rem] sm:text-[8rem] font-heading font-black leading-none text-destructive/10 select-none pointer-events-none"
        aria-hidden="true"
      >
        {number}
      </span>

      {/* Content */}
      <div className="relative z-10">
        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-destructive/10 text-destructive mb-5">
          <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
        </div>
        <h3 className="font-semibold text-lg text-foreground mb-2">
          {title}
        </h3>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
}

const staggerClasses = ["stagger-1", "stagger-2", "stagger-3"];

export function Problem() {
  const { t } = useTranslation();

  const problems = [
    {
      icon: FileQuestion,
      number: "01",
      title: t("problem.itemNamesUnclear"),
      description: t("problem.itemNamesUnclearDesc"),
    },
    {
      icon: Search,
      number: "02",
      title: t("problem.findingReceiptsTakesTime"),
      description: t("problem.findingReceiptsTakesTimeDesc"),
    },
    {
      icon: Frown,
      number: "03",
      title: t("problem.returnsFeelStressful"),
      description: t("problem.returnsFeelStressfulDesc"),
    },
  ];

  return (
    <section className="relative py-20 sm:py-28 bg-background noise-bg overflow-hidden">
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-16 animate-fade-up">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 text-balance">
            {t("problem.receiptsShouldNotBeHard").split(/{[^}]*}/)[0]}
            <em className="font-heading text-destructive">{t("problem.hardToUse")}</em>
            {t("problem.receiptsShouldNotBeHard").split(/{[^}]*}/)[1] || ""}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t("problem.weveAllBeenThere")}
          </p>
        </div>

        {/* Problem cards */}
        <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 lg:gap-8 max-w-5xl mx-auto">
          {problems.map((problem, index) => (
            <ProblemCard
              key={problem.number}
              icon={problem.icon}
              number={problem.number}
              title={problem.title}
              description={problem.description}
              staggerClass={staggerClasses[index]}
            />
          ))}
        </div>

        {/* Divider */}
        <div className="max-w-xs mx-auto my-14 sm:my-16 animate-fade-up stagger-4">
          <div className="h-px bg-gradient-to-r from-transparent via-destructive/30 to-transparent" />
        </div>

        {/* Resolution statement */}
        <div className="text-center animate-fade-up stagger-5">
          <p className="text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold text-gradient">
            {t("problem.refoundFixesThat")}
          </p>
        </div>
      </div>
    </section>
  );
}
